import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  increment,
  arrayUnion
} from 'firebase/firestore'
import { db } from '@/firebase'
import { normCouponFromDoc } from '@/lib/couponSchema'
import { parseItemPrice } from '@/lib/restaurantPublicProfile'

/** @typedef {ReturnType<typeof normCouponFromDoc>} MenuCoupon */

/*
 * Coupon rules shared with the Madentak app (keep both in step):
 * - look up code + city WITHOUT limit 1; keep active, channel, service and
 *   restaurant matches; prefer a restaurant's own code over a city-wide one.
 * - applies_to: items → % of food; total → % of food + delivery;
 *   delivery → % of delivery; cap with upto > 0; min_order is on the food subtotal.
 * - a delivery-only code on a hall / pickup order is rejected, not silently 0.
 * - restaurant-funded codes don't shrink the app service fee (fee before discount).
 */

export const COUPON_ERRORS = {
  invalid: 'كود غير صالح',
  expired: 'انتهت صلاحية الكوبون',
  used: 'استخدمت الكوبون من قبل',
  wrongEntity: 'الكوبون غير صالح لهذا الكيان',
  inactive: 'الكود ده متوقف حالياً',
  wrongChannel: 'الكود ده مش متاح هنا',
  wrongService: 'الكود ده مش متاح للخدمة دي',
  deliveryOnly: 'الكود ده للتوصيل بس — مش بيشتغل على طلبات الصالة أو الاستلام'
}

export function couponMinOrderError(min) {
  return `الحد الأدنى للطلب لاستخدام الكود هو ${Math.round(Number(min) || 0)} ج.م`
}

export async function fetchCouponsByCode(code, city) {
  const normalizedCode = String(code || '').trim().toUpperCase()
  const normalizedCity = String(city || '').trim()
  if (!normalizedCode || !normalizedCity) return []

  const snap = await getDocs(
    query(
      collection(db, 'Coupons'),
      where('code', '==', normalizedCode),
      where('where', '==', normalizedCity)
    )
  )
  return snap.docs.map((d) => normCouponFromDoc(d.id, d.data()))
}

/** @deprecated returns the first match only — use applyMenuCoupon */
export async function fetchCouponByCode(code, city) {
  const list = await fetchCouponsByCode(code, city)
  return list[0] || null
}

function isBasedOnCount(coupon) {
  return coupon?.based_on_count === true
}

function isRestaurantSpecificFor(coupon, restId) {
  return (
    coupon?.for_all_res === false &&
    Array.isArray(coupon.restaurants) &&
    coupon.restaurants.includes(restId)
  )
}

export function couponAppliesTo(coupon) {
  const v = String(coupon?.applies_to || '').trim()
  if (v === 'items' || v === 'total' || v === 'delivery') return v
  return coupon?.offer_in_total === true ? 'total' : 'delivery'
}

export function couponFundedBy(coupon) {
  return coupon?.funded_by === 'restaurant' ? 'restaurant' : 'app'
}

/**
 * @param {MenuCoupon} coupon
 * @param {{ userId?: string, restaurantId?: string, channel?: string, service?: string,
 *   itemTotal?: number, orderType?: string }} ctx
 *   itemTotal → checks min_order; orderType hall/pickup → rejects delivery-only codes.
 * @returns {{ ok: true, coupon: MenuCoupon } | { ok: false, error: string }}
 */
export function validateCouponForCheckout(coupon, ctx = {}) {
  if (!coupon) {
    return { ok: false, error: COUPON_ERRORS.invalid }
  }

  const uid = String(ctx.userId || '').trim()
  const restId = String(ctx.restaurantId || '').trim()
  const channel = ctx.channel || 'menu'
  const service = ctx.service || 'restaurant'

  if (coupon.active === false) {
    return { ok: false, error: COUPON_ERRORS.inactive }
  }

  if (Array.isArray(coupon.channels) && coupon.channels.length && !coupon.channels.includes(channel)) {
    return { ok: false, error: COUPON_ERRORS.wrongChannel }
  }

  if (Array.isArray(coupon.services) && !coupon.services.includes(service)) {
    return { ok: false, error: COUPON_ERRORS.wrongService }
  }

  if (coupon.for_all_res === false && !isRestaurantSpecificFor(coupon, restId)) {
    return { ok: false, error: COUPON_ERRORS.wrongEntity }
  }

  if (isBasedOnCount(coupon) && (coupon.count ?? 0) <= 0) {
    return { ok: false, error: COUPON_ERRORS.expired }
  }

  if (coupon.based_on_date === true) {
    const endDate = coupon.end_date instanceof Date ? coupon.end_date : null
    if (!endDate || new Date() > endDate) {
      return { ok: false, error: COUPON_ERRORS.expired }
    }
  }

  if (uid && Array.isArray(coupon.users) && coupon.users.includes(uid)) {
    return { ok: false, error: COUPON_ERRORS.used }
  }

  if (ctx.itemTotal != null && Number(coupon.min_order) > 0) {
    if (parseItemPrice(ctx.itemTotal) < Number(coupon.min_order)) {
      return { ok: false, error: couponMinOrderError(coupon.min_order) }
    }
  }

  const noDelivery = ctx.orderType === 'hall' || ctx.orderType === 'pickup'
  if (noDelivery && couponAppliesTo(coupon) === 'delivery') {
    return { ok: false, error: COUPON_ERRORS.deliveryOnly }
  }

  return { ok: true, coupon }
}

/**
 * Picks the usable coupon among docs sharing one code in a city: a
 * restaurant's own code wins over a city-wide one. When none is usable the
 * error of the closest candidate is returned.
 */
export function pickCouponForCheckout(candidates, ctx = {}) {
  const list = Array.isArray(candidates) ? candidates : []
  if (!list.length) return { ok: false, error: COUPON_ERRORS.invalid }
  const restId = String(ctx.restaurantId || '').trim()
  const ranked = [...list].sort(
    (a, b) => Number(isRestaurantSpecificFor(b, restId)) - Number(isRestaurantSpecificFor(a, restId))
  )
  let best = null
  for (const c of ranked) {
    const res = validateCouponForCheckout(c, ctx)
    if (res.ok) return res
    // "not for this restaurant" says less than a reason found on a matching code
    if (!best || (best.error === COUPON_ERRORS.wrongEntity && res.error !== COUPON_ERRORS.wrongEntity)) {
      best = res
    }
  }
  return best || { ok: false, error: COUPON_ERRORS.invalid }
}

/**
 * Discount split for the order doc.
 * @returns {{ items: number, delivery: number, total: number }}
 */
export function calculateMenuCouponSplit({ appliedCoupon, itemTotal, deliveryCost }) {
  if (!appliedCoupon) return { items: 0, delivery: 0, total: 0 }

  const perc = (parseFloat(String(appliedCoupon.discount ?? '0')) || 0) / 100
  const items = Math.max(0, parseItemPrice(itemTotal))
  const delivery = Math.max(0, parseItemPrice(deliveryCost))
  const appliesTo = couponAppliesTo(appliedCoupon)

  let onItems = 0
  let onDelivery = 0
  if (appliesTo === 'items') {
    onItems = items * perc
  } else if (appliesTo === 'total') {
    onItems = items * perc
    onDelivery = delivery * perc
  } else {
    onDelivery = delivery * perc
  }

  const total = onItems + onDelivery
  const upto = Number(appliedCoupon.upto)
  if (Number.isFinite(upto) && upto > 0 && total > upto) {
    // the cap keeps the food / delivery share
    const ratio = upto / total
    onItems *= ratio
    onDelivery *= ratio
  }

  const round2 = (n) => Math.round(Math.max(0, n) * 100) / 100
  const r = { items: round2(onItems), delivery: round2(onDelivery) }
  return { ...r, total: round2(r.items + r.delivery) }
}

/** حساب قيمة الخصم — نفس منطق تطبيق الموبايل */
export function calculateMenuCouponDiscount(args) {
  return calculateMenuCouponSplit(args).total
}

/** Order fields that record the coupon (shared contract with the app). */
export function couponOrderFields(appliedCoupon, split) {
  if (!appliedCoupon) {
    return {
      couponId: null,
      couponFundedBy: null,
      couponAppliesTo: null,
      couponOwnerEntityId: null,
      discountItems: 0,
      discountDelivery: 0
    }
  }
  return {
    couponId: String(appliedCoupon.id || '').trim() || null,
    couponFundedBy: couponFundedBy(appliedCoupon),
    couponAppliesTo: couponAppliesTo(appliedCoupon),
    couponOwnerEntityId: String(appliedCoupon.owner_entity_id || '').trim() || null,
    discountItems: Number(split?.items) || 0,
    discountDelivery: Number(split?.delivery) || 0
  }
}

/**
 * @param {string} code
 * @param {{ city: string, userId?: string, restaurantId?: string, service?: string,
 *   itemTotal?: number, orderType?: string }} ctx
 */
export async function applyMenuCoupon(code, ctx = {}) {
  const candidates = await fetchCouponsByCode(code, ctx.city)
  return pickCouponForCheckout(candidates, { channel: 'menu', ...ctx })
}

/** @param {MenuCoupon|string} couponOrId pass the coupon so count only drops on count-limited codes */
export async function consumeCouponAfterOrder(couponOrId, userId) {
  const coupon = couponOrId && typeof couponOrId === 'object' ? couponOrId : null
  const id = String(coupon ? coupon.id : couponOrId || '').trim()
  const uid = String(userId || '').trim()
  if (!id || !uid) return

  const patch = { users: arrayUnion(uid) }
  if (!coupon || isBasedOnCount(coupon)) patch.count = increment(-1)
  await updateDoc(doc(db, 'Coupons', id), patch)
}
